import {
  ButtonGroup,
  DeprecatedLink,
  getColumnSortHeadCellProps,
  getRowInteractionRowProps,
  Table,
} from "@resource/atlas";
import BulkActionBar from "components/BulkActionBar/BulkActionBar";
import { Pagination } from "components/Pagination/Pagination";
import GlobalFilter from "components/Table/GlobalFilter";
import usePagination from "components/Table/hooks/usePagination";
import useQueryParamsState, {
  DEFAULT_PAGE_SIZE,
} from "components/Table/hooks/useQueryParamState";
import useRowSelect from "components/Table/hooks/useRowSelect";
import NoTableResults from "components/Table/NoTableResults";
import { OrderByArg } from "generated/schemaTypes";
import _ from "lodash";
import { ReactNode, useEffect, useMemo } from "react";
import {
  Column,
  Row,
  useFilters,
  useFlexLayout,
  useGlobalFilter,
  useResizeColumns,
  useSortBy,
  useTable,
} from "react-table";

import MultiSelectFilter from "./MultiSelectFilter";
import SingleSelectFilter from "./SingleSelectFilter";
import {
  PaginatedTableFieldFilterTypes,
  PaginatedTableFilters,
  PaginatedTablePaginationProps,
} from "./types";

export interface PaginatedTableProps<T extends object> {
  data: T[];
  loading: boolean;
  paginationProps: PaginatedTablePaginationProps;
  filters?: PaginatedTableFilters;
  headerRightActions?: ReactNode;
  allIds: string[];
  totalRecordCount: number;
  columns: readonly Column<T>[];
  hiddenColumns?: string[];
  getRowId: (arg: T) => string;
  onRowClick?: (arg: Row<T>) => void;
  setSelectedIds?: (arg: string[]) => void;
  bulkActionBarContent?: ReactNode;
  bulkActionItemDisplayText?: string;
}

function PaginatedTable<T extends object>({
  data,
  loading,
  paginationProps,
  filters,
  headerRightActions,
  allIds,
  totalRecordCount,
  columns,
  hiddenColumns,
  getRowId,
  onRowClick,
  setSelectedIds,
  bulkActionBarContent,
  bulkActionItemDisplayText = "Item",
}: PaginatedTableProps<T>) {
  // Lets us track state updates on the field filters field efficiently
  // so we don't have to worry about wrapping the stuff in a memo from the component consuming this
  const fieldFiltersStateCheck = useMemo(
    () =>
      filters?.field
        ?.map((value) => `${value.id}-${value?.options?.length ?? 0}`)
        .join("|"),
    [filters?.field]
  );

  const fieldFilters = useMemo(
    () =>
      filters?.field?.map((fieldFilter) => ({
        id: fieldFilter.id,
        value: fieldFilter.initialValue ?? fieldFilter.defaultValue,
      })) ?? [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fieldFiltersStateCheck]
  );

  // Lets us track state updates on the orderBy field efficiently
  // so we don't have to worry about wrapping the stuff in a memo from the component consuming this
  const sortByStateCheck = useMemo(
    () =>
      filters?.orderBy
        ?.map((value) => `${Object.keys(value)[0]}-${Object.values(value)[0]}`)
        .join("|"),
    [filters?.orderBy]
  );

  const sortBy = useMemo(
    () =>
      _(filters?.orderBy)
        .flatten()
        .map((value) => ({
          id: _(value).keys().head() as string,
          desc: _(value).values().head() === OrderByArg.desc,
        }))
        .value() ?? [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sortByStateCheck]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    setGlobalFilter,
    setPageSize,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    state: tableState,
    prepareRow,
    setAllFilters,
    setFilter,
    toggleAll,
  } = useTable(
    {
      columns,
      hiddenColumns,
      data,
      allIds,
      totalCount: totalRecordCount,
      manualGlobalFilter: true,
      autoResetGlobalFilter: true,
      getRowId,
      initialState: {
        globalFilter: filters?.search ?? "",
        filters: fieldFilters,
        pageIndex:
          (paginationProps?.offset ?? 0) / (paginationProps?.limit ?? 0),
        pageSize: paginationProps?.limit ?? DEFAULT_PAGE_SIZE,
        sortBy,
        isLoading: loading,
      },
      autoResetSelectedRows: false,
      pageCount: Math.ceil(
        allIds.length / (paginationProps?.limit ?? DEFAULT_PAGE_SIZE)
      ),
      manualPagination: true,
      autoResetPage: false,
      manualSortBy: true,
      autoResetSortBy: false,
      manualFilters: true,
      autoResetFilters: false,
      disableSortRemove: true,
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    usePagination,
    useQueryParamsState,
    useRowSelect,
    useFlexLayout,
    useResizeColumns
  );

  const isFiltered = useMemo(
    () =>
      tableState.globalFilter ||
      tableState.filters.some(
        (tableFilter) =>
          filters?.field?.find(
            (fieldFilter) => fieldFilter.id === tableFilter.id
          )?.defaultValue !== tableFilter.value
      ),
    [tableState.globalFilter, tableState.filters, filters]
  );
  const selectedIds = useMemo(
    () => _(tableState.selectedRowIds).pickBy().keys().value(),
    [tableState.selectedRowIds]
  );

  useEffect(() => setSelectedIds?.(selectedIds), [selectedIds, setSelectedIds]);

  const numSelectedRows = selectedIds.length;
  const hasResults = !!totalRecordCount;

  const clearFilters = () => {
    setGlobalFilter("");
    setAllFilters(
      () =>
        filters?.field?.map((fieldFilter) => ({
          id: fieldFilter.id,
          value: fieldFilter.defaultValue,
        })) ?? []
    );
  };

  return (
    <div>
      <div className="flex justify-between mt-5 w-full">
        <GlobalFilter
          className="mb-2"
          placeholder="Search"
          globalFilter={tableState.globalFilter ?? ""}
          setGlobalFilter={setGlobalFilter}
        />
        <div className="flex justify-end w-full space-x-4">
          <div className="flex space-x-1 items-center">
            {isFiltered && (
              <DeprecatedLink
                variant="monochrome"
                href="#"
                className="flex-shrink-0 mr-6"
                onPress={() => clearFilters()}
              >
                Clear all
              </DeprecatedLink>
            )}
            <ButtonGroup isGhost isDropdown>
              {filters?.field?.map((fieldFilter) => {
                const currentValue = tableState.filters.find(
                  (filter) => filter.id === fieldFilter.id
                )?.value;
                if (
                  fieldFilter.type ===
                  PaginatedTableFieldFilterTypes.singleSelect
                ) {
                  return (
                    <SingleSelectFilter
                      fieldId={fieldFilter.id}
                      options={fieldFilter.options}
                      value={
                        (currentValue || fieldFilter.defaultValue) as string
                      }
                      setFilter={setFilter}
                      disabled={fieldFilter.disabled}
                    />
                  );
                }
                if (
                  fieldFilter.type ===
                  PaginatedTableFieldFilterTypes.multiSelect
                ) {
                  return (
                    <MultiSelectFilter
                      fieldId={fieldFilter.id}
                      label={fieldFilter.label}
                      options={fieldFilter.options}
                      value={
                        (currentValue || fieldFilter.defaultValue) as string[]
                      }
                      setFilter={setFilter}
                    />
                  );
                }
                return null;
              })}
            </ButtonGroup>
            {headerRightActions}
          </div>
        </div>
      </div>
      <Table.Root {...getTableProps()}>
        <Table.Head role="rowgroup">
          {_.map(headerGroups, (headerGroup) => (
            <Table.HeadRow {...headerGroup.getHeaderGroupProps()}>
              {_.map(headerGroup.headers, (column) => (
                <Table.HeadCell
                  {...column.getHeaderProps()}
                  {...getColumnSortHeadCellProps(column)}
                >
                  {column.render("Header")}
                </Table.HeadCell>
              ))}
            </Table.HeadRow>
          ))}
        </Table.Head>
        <Table.Body {...getTableBodyProps()}>
          {_.map(rows, (row) => {
            prepareRow(row);
            return (
              <Table.Row
                {...row.getRowProps()}
                {...getRowInteractionRowProps(
                  { state: tableState, row },
                  {
                    onClick: () => onRowClick?.(row),
                  }
                )}
              >
                {row.cells.map((cell) => (
                  <Table.Cell {...cell.getCellProps()}>
                    {cell.render("Cell")}
                  </Table.Cell>
                ))}
              </Table.Row>
            );
          })}
          <NoTableResults
            loading={loading}
            hasResults={hasResults}
            isFiltered={isFiltered}
            clearFilters={clearFilters}
          />
        </Table.Body>
      </Table.Root>
      <BulkActionBar
        isVisible={!!numSelectedRows}
        selectedCount={numSelectedRows}
        totalCount={totalRecordCount ?? 0}
        itemDisplayText={bulkActionItemDisplayText}
        isLoading={loading}
        onSelectAll={() => {
          toggleAll(true);
        }}
        onClearAll={() => toggleAll(false)}
        onClose={() => toggleAll(false)}
      >
        {bulkActionBarContent}
      </BulkActionBar>
      {hasResults ? (
        <Pagination
          totalCount={totalRecordCount}
          pageSize={tableState.pageSize}
          pageIndex={tableState.pageIndex}
          isLoading={loading}
          onPrevPage={() => {
            previousPage();
          }}
          onNextPage={() => {
            nextPage();
          }}
          canNextPage={canNextPage}
          canPreviousPage={canPreviousPage}
          onPageSizeChange={(newPageSize: number) => {
            setPageSize(newPageSize);
          }}
        />
      ) : null}
    </div>
  );
}

export default PaginatedTable;
